import * as d3 from 'd3';
import { useCallback, useEffect, useRef, useState } from 'react';
import useMobileLayout from '@/hooks/use-mobile-layout';
type BarItem = {
  label: string;
  value: number;
  color?: string;
};
type HorizontalBarChartProps = {
  items: BarItem[];
  currency?: string;
  sorted?: boolean;
};
const DEFAULT_COLOR = '#64748b';

// Guards identical to bar-chart.tsx: AI-emitted currency codes and numeric
// values aren't guaranteed to be valid.
const safeCurrencyFormat = (options: Intl.NumberFormatOptions, currency: string) => {
  try {
    return new Intl.NumberFormat('en-US', {
      ...options,
      currency
    });
  } catch {
    return new Intl.NumberFormat('en-US', {
      ...options,
      currency: 'USD'
    });
  }
};
const toFinite = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const fmtAxis = (v: number, currency = 'USD') => safeCurrencyFormat({
  style: 'currency',
  notation: 'compact',
  maximumFractionDigits: 1
}, currency).format(v);
const fmtValue = (v: number, currency = 'USD') => safeCurrencyFormat({
  style: 'currency',
  maximumFractionDigits: 0
}, currency).format(v);
const HorizontalBarChart = ({
  items = [],
  currency = 'USD',
  sorted = true
}: HorizontalBarChartProps) => {
  const isMobile = useMobileLayout();
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // biome-ignore lint/plugin: ref-callback identity gates React attach/detach for the ResizeObserver
  const measuredRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      containerRef.current = node;
      setWidth(node.clientWidth);
      const ro = new ResizeObserver(entries => {
        const w = entries[0]?.contentRect.width;
        if (w && w > 0) setWidth(w);
      });
      ro.observe(node);
      return () => ro.disconnect();
    }
  }, []);
  const sortedItems = (() => {
    const coerced = items.map(it => ({
      ...it,
      value: toFinite(it.value)
    }));
    return sorted ? coerced.sort((a, b) => b.value - a.value) : coerced;
  })();
  useEffect(() => {
    if (!svgRef.current || !sortedItems.length || !width) return;
    const el = svgRef.current;
    const svg = d3.select(el);
    svg.selectAll('*').remove();
    const W = width;
    const barH = 28;
    const gap = 8;
    // A phone's label column would leave ~9 characters and a hover-only tooltip for the rest, so there
    // each label gets its own full-width line above its bar.
    const labelLineH = isMobile ? 16 : 0;
    const labelW = isMobile ? W : Math.min(120, W * 0.25);
    const valueW = 64;
    const margin = {
      top: 8,
      right: valueW + 12,
      bottom: 8,
      left: isMobile ? 0 : labelW + 12
    };
    const iw = W - margin.left - margin.right;
    const rowH = barH + labelLineH;
    const ih = sortedItems.length * (rowH + gap) - gap;
    const H = ih + margin.top + margin.bottom;
    svg.attr('width', W).attr('height', H);
    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);
    const xMax = d3.max(sortedItems, d => d.value) ?? 0;
    const x = d3.scaleLinear().domain([0, xMax * 1.05]).range([0, iw]);
    const y = d3.scaleBand().domain(sortedItems.map(d_0 => d_0.label)).range([0, ih]).padding(gap / (rowH + gap));

    // Labels
    g.selectAll('text.label').data(sortedItems).join('text').attr('class', 'label').attr('x', isMobile ? 0 : -12).attr('y', d_1 => (y(d_1.label) ?? 0) + (isMobile ? labelLineH / 2 - 2 : y.bandwidth() / 2)).attr('text-anchor', isMobile ? 'start' : 'end').attr('dominant-baseline', 'middle').attr('font-size', 12).attr('font-family', 'inherit').attr('fill', 'var(--text-color-secondary)');

    // Wrap long labels
    g.selectAll<SVGTextElement, BarItem>('text.label').each(function (d_2) {
      const textEl = d3.select(this);
      const maxW = labelW;
      textEl.text(d_2.label);
      if (this.getComputedTextLength() > maxW) {
        let truncated = d_2.label;
        while (truncated.length > 3 && this.getComputedTextLength() > maxW) {
          truncated = truncated.slice(0, -1);
          textEl.text(`${truncated}…`);
        }
      }
    });

    // Bars
    g.selectAll('rect.bar').data(sortedItems).join('rect').attr('class', 'bar').attr('x', 0).attr('y', d_3 => (y(d_3.label) ?? 0) + labelLineH).attr('width', d_4 => Math.max(0, x(d_4.value))).attr('height', y.bandwidth() - labelLineH).attr('fill', d_5 => d_5.color ?? DEFAULT_COLOR).attr('rx', 3).attr('opacity', 0.85);

    // Value labels
    g.selectAll('text.value').data(sortedItems).join('text').attr('class', 'value').attr('x', d_6 => x(d_6.value) + 8).attr('y', d_7 => (y(d_7.label) ?? 0) + labelLineH + (y.bandwidth() - labelLineH) / 2).attr('dominant-baseline', 'middle').attr('font-size', 11).attr('font-weight', 500).attr('font-family', 'inherit').attr('fill', 'var(--text-color-tertiary)').text(d_8 => fmtAxis(d_8.value, currency));

    // Hit areas for hover
    g.selectAll('rect.hit').data(sortedItems).join('rect').attr('class', 'hit').attr('x', -margin.left).attr('y', d_9 => y(d_9.label) ?? 0).attr('width', W).attr('height', y.bandwidth()).attr('fill', 'transparent').attr('cursor', 'pointer').on('mouseenter', (_, d_10) => {
      const idx = sortedItems.indexOf(d_10);
      setHoveredIndex(idx);
    }).on('mouseleave', () => setHoveredIndex(null));
    return () => {
      svg.selectAll('*').remove();
    };
  }, [sortedItems, currency, width, isMobile]);

  // Highlight effect
  useEffect(() => {
    if (!svgRef.current) return;
    const svg_0 = d3.select(svgRef.current);
    svg_0.selectAll<SVGRectElement, BarItem>('rect.bar').attr('opacity', (__0, i) => hoveredIndex === null ? 0.85 : i === hoveredIndex ? 1 : 0.4);
  }, [hoveredIndex]);
  return <div className="w-full">
      <div ref={measuredRef} className="relative">
        <svg ref={svgRef} role="img" aria-label="Horizontal bar chart" style={{
        display: 'block',
        width: '100%'
      }} />

        {hoveredIndex !== null && sortedItems[hoveredIndex] && <div className="pointer-events-none absolute z-10 rounded-md px-3 py-1.5 shadow-md" style={{
        right: 12,
        top: 8,
        backgroundColor: 'var(--text-color-primary)'
      }}>
            <p className="text-[11px] font-semibold" style={{
          color: 'var(--background-color-primary)'
        }}>
              {sortedItems[hoveredIndex].label}
            </p>
            <p className="text-[10px]" style={{
          color: '#a0a0a0'
        }}>
              {fmtValue(sortedItems[hoveredIndex].value, currency)}
            </p>
          </div>}
      </div>
    </div>;
};
export default HorizontalBarChart;